import {useEffect, useMemo, useRef} from 'react'

import type {PageTypeForGroupedItems} from '../../state-providers/memex-items/queries/query-keys'
import {usePaginatedMemexItemsQuery} from '../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import useIsVisible from '../board/hooks/use-is-visible'

export const useVisiblePagination = ({groupId}: {groupId?: string}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const {isVisible} = useIsVisible({ref})
  const prevIsVisible = useRef<boolean>(false)
  const {
    isFetchingNextPage,
    isFetchingNextPageForGroupedItems,
    fetchNextPage,
    fetchNextPageForGroupedItems,
    hasNextPage,
    hasNextPageForGroupedItems,
  } = usePaginatedMemexItemsQuery()
  useEffect(() => {
    // isVisible remains briefly true after fetchNextPage completes.
    // This check prevents an extra request from being fired.
    if (prevIsVisible.current === isVisible) return
    prevIsVisible.current = isVisible
    if (!isVisible) return
    const pageType: PageTypeForGroupedItems | undefined = groupId ? {groupId} : undefined
    if (pageType && hasNextPageForGroupedItems(pageType) && !isFetchingNextPageForGroupedItems(pageType)) {
      fetchNextPageForGroupedItems(pageType)
    } else if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [
    isVisible,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    groupId,
    fetchNextPageForGroupedItems,
    hasNextPageForGroupedItems,
    isFetchingNextPageForGroupedItems,
  ])

  return useMemo(
    () => ({
      ref,
      hasNextPage: groupId ? hasNextPageForGroupedItems({groupId}) : hasNextPage,
    }),
    [groupId, hasNextPage, hasNextPageForGroupedItems],
  )
}
